// MUI-free copy of Ui-components/Dynamic-screens.
import {memo} from 'react';
import type {CSSProperties} from 'react';
import Link from 'next/link';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import {cdnImage} from '@/util/cdnImage';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './DynamicScreens.module.scss';

const bitcoin = cdnImage('white-label-crypto-exchage/bitcoin.webp');

export interface DynamicScreensButtonData {
    /** HTML string (parsed), or several, each in its own block. */
    header?: string | string[];
    /** HTML strings, one chip each. */
    button?: string[];
}

export interface DynamicScreensProps {
    imageSrc: StaticImageData | string;
    alt: string;
    /** HTML string, parsed. */
    heading?: string;
    /** HTML string, parsed. */
    subHeading?: string;
    /** HTML strings, parsed. */
    subPoints?: string[];
    reverseOrder?: boolean;
    buttonData?: DynamicScreensButtonData;
    buttonLabel?: string;
    /** `nft-button` switches to the orange gradient CTA; any other value is passed through as a class. */
    buttonClass?: string;
    /** HTML strings, parsed. */
    checkItems?: string[];
    /** Click handler — or, with `caseStudy`, the href the button links to (new tab). */
    openForm?: (() => void) | string;
    imageClass?: string;
    isFullWidth?: boolean;
    caseStudy?: boolean;
    /** Flat inline styles for the root box (stand-in for the old `wrapperSx`; never used by any page). */
    wrapperSx?: CSSProperties;
    /** Flat inline styles for the main <img> (stand-in for the old `imageSx`; never used by any page). */
    imageSx?: CSSProperties;
    /** Heading tag. Defaults to h2; pass 'h3' when the screens sit under their own section <h2> (as the original). */
    headingTag?: 'h2' | 'h3';
}

// MUI <DoneIcon /> path (@mui/icons-material/Done).
const DONE_PATH = 'M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z';

const DoneIcon = ({className}: {className: string}) => (
    <svg className={className} focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="DoneIcon">
        <path d={DONE_PATH} />
    </svg>
);

const renderHeader = (header: DynamicScreensButtonData['header']) => {
    if (Array.isArray(header)) {
        return header.map((item, idx) => (
            <div key={idx + 1} className={styles.mb8}>
                {parse(item)}
            </div>
        ));
    }
    return header ? parse(header) : '';
};

interface ImageColumnProps {
    reverseOrder?: boolean;
    imageSrc: StaticImageData | string;
    alt: string;
    imageClass?: string;
    imageSx?: CSSProperties;
    isFullWidth: boolean;
}

const ImageColumn = ({reverseOrder, imageSrc, alt, imageClass, imageSx, isFullWidth}: ImageColumnProps) => (
    <div className={`${styles.item} ${styles.imageItem}`}>
        {/* The reverse branch's Box justifyContent="flex-end" never applied: the global .justify-center
            (justify-content: center) won the tie over Emotion, so it is left out. */}
        <div className={reverseOrder ? ' justify-center' : 'justify-center'}>
            <div className={`bitcoin-display-icon-wrapper ${styles.iconWrapper}`}>
                <OptimizedImage
                    src={imageSrc}
                    alt={alt}
                    className={`banner-img decoding-img ${imageClass}`}
                    width={500}
                    height={380}
                    priority={false}
                    style={imageSx}
                />
                {!isFullWidth && (
                    <OptimizedImage
                        src={bitcoin}
                        alt="bitcoin"
                        className={reverseOrder ? 'bitcoin-dynamic-img reverse-icon-wrapper' : 'bitcoin-dynamic-img bitcoin-icon-wrapper'}
                        width={100}
                        height={100}
                        priority={false}
                    />
                )}
            </div>
        </div>
    </div>
);

const DynamicScreens = ({
    imageSrc,
    alt,
    heading,
    subHeading,
    subPoints,
    reverseOrder,
    buttonData,
    buttonLabel,
    buttonClass,
    checkItems,
    openForm,
    imageClass,
    isFullWidth = true,
    caseStudy,
    wrapperSx,
    imageSx,
    headingTag: HeadingTag = 'h2'
}: DynamicScreensProps) => {
    const isCtaButton = buttonClass === 'nft-button';
    const buttonClassName = `${isCtaButton ? '' : buttonClass} dynamic-screen-button animated-button ${
        isCtaButton ? styles.nftButton : styles.button
    }`;
    const subTextClass = `spectrum-sub-heading color-black dynamic-screen-sub-heading ${styles.text} ${styles.screenSub}`;
    const imageColumn = (
        <ImageColumn
            reverseOrder={reverseOrder}
            imageSrc={imageSrc}
            alt={alt}
            imageClass={imageClass}
            imageSx={imageSx}
            isFullWidth={isFullWidth}
        />
    );

    return (
        <div
            className={
                reverseOrder
                    ? `backGroundColor-Reverse dynamic--screen-wrapper-main ${styles.root} ${styles.reverse}`
                    : `backGroundColor-Reverse1 dynamic--screen-wrapper-main ${styles.root} ${styles.reverse1}`
            }
            style={wrapperSx}
        >
            <div className={!reverseOrder ? `reverse-dynamic-img ${styles.grid} ${styles.wrapReverse}` : ` ${styles.grid}`}>
                {!reverseOrder && imageColumn}
                <div className={`${styles.item} ${styles.textItem}`}>
                    <div className="decoding-header-wrapper solutions">
                        <HeadingTag className={`spectrum-heading color-black ${styles.text} ${styles.headingPb}`}>
                            {heading ? parse(heading) : ''}
                        </HeadingTag>

                        {subHeading && (
                            <div className={styles.pb16}>
                                <p
                                    className={`spectrum-sub-heading color-black  dynamic-between-subheading ${
                                        reverseOrder ? 'color-orange' : 'dynamic-color-blue'
                                    } ${styles.text} ${styles.betweenSub}`}
                                >
                                    {parse(subHeading)}
                                </p>
                            </div>
                        )}
                        {buttonData && (
                            <div>
                                {/* A header list renders block rows, which a <p> can't contain (invalid HTML, hydration mismatch). */}
                                {Array.isArray(buttonData.header) ? (
                                    <div className={subTextClass}>{renderHeader(buttonData.header)}</div>
                                ) : (
                                    <p className={subTextClass}>{renderHeader(buttonData.header)}</p>
                                )}
                                {buttonData.button && (
                                    <div className="dynamic-screen-button-wrapper">
                                        {buttonData.button.map((buttonItem, index) => (
                                            <div className="dynamic-button-box" key={index + 1}>
                                                {buttonItem ? parse(buttonItem) : ''}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {subPoints?.map((item, index) => (
                            <div key={index + 1} className={`points-dynamic-wrapper ${styles.pb8} ${styles.points}`}>
                                <p className={subTextClass}>{item ? parse(item) : ''}</p>
                            </div>
                        ))}
                        {checkItems && (
                            <div className={styles.checkGrid}>
                                {checkItems.map((item, indexData) => (
                                    <div
                                        key={indexData + 1}
                                        className={isFullWidth ? styles.checkItem : `${styles.checkItem} ${styles.checkItemHalf}`}
                                    >
                                        <div className={styles.checkRow}>
                                            <DoneIcon
                                                className={
                                                    reverseOrder ? `color-orange ${styles.icon}` : `${styles.icon} ${styles.iconBlue}`
                                                }
                                            />
                                            <p className={`dynamic-screen-sub-heading ${styles.text} ${styles.screenSub}`}>
                                                {item ? parse(item) : ''}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {buttonLabel &&
                            (caseStudy ? (
                                <Link target="_blank" href={typeof openForm === 'string' ? openForm : ''}>
                                    <button type="button" className={buttonClassName}>
                                        {buttonLabel}
                                    </button>
                                </Link>
                            ) : (
                                <button
                                    type="button"
                                    className={buttonClassName}
                                    onClick={typeof openForm === 'function' ? openForm : undefined}
                                >
                                    {buttonLabel}
                                </button>
                            ))}
                    </div>
                </div>
                {reverseOrder && imageColumn}
            </div>
        </div>
    );
};

export default memo(DynamicScreens);
